import { useCallback, useEffect } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import {
    type PickingResultData,
    type PickingSession,
    type StartPickingOptions,
    usePickingSessionRef,
} from "contexts/pickingSession";
import { measureActions } from "features/measure";
import { useConfirmCurrentModeChanges } from "features/toolMode/useConfirmCurrentModeChanges";

import { renderActions, selectPicker } from "../renderSlice";
import { Picker } from "../types";
import { buildTolerance } from "./buildPickingTolerance";

export { buildTolerance };

/**
 * Returns a function that starts a snap-enabled picking session and resolves with the picked point.
 * Only one session can be active at a time - starting a new one rejects the previous promise with
 * an `AbortError`. Assigned to `webviewer.startPickingHook` in the bridge.
 */
export function useStartPicking() {
    const dispatch = useAppDispatch();
    const sessionRef = usePickingSessionRef();
    const confirmCurrentModeChanges = useConfirmCurrentModeChanges();
    const picker = useAppSelector(selectPicker);

    // Abort the active session when the picker changes away from Picking (e.g. another tool is
    // activated, or the view is reset), so the promise doesn't hang once the session is no longer
    // in control. No-op when no session is active.
    useEffect(() => {
        if (picker !== Picker.SdkPicking && sessionRef.current) {
            sessionRef.current.reject(new DOMException("Picking cancelled because the picker changed", "AbortError"));
        }
    }, [picker, sessionRef]);

    return useCallback(
        (options: StartPickingOptions = {}): Promise<PickingResultData> => {
            // Commit and exit any active tool mode (measure/point-line/etc.) before starting the
            // picking session. No-op when no tool mode is active.
            confirmCurrentModeChanges();

            // Supersede any in-flight session.
            const previous = sessionRef.current;
            if (previous) {
                sessionRef.current = null;
                previous.reject(new DOMException("Superseded by a new startPicking call", "AbortError"));
            }

            const { abortSignal, onHover } = options;
            if (abortSignal?.aborted) {
                dispatch(renderActions.stopPicker(Picker.SdkPicking));
                return Promise.reject(new DOMException("Aborted", "AbortError"));
            }

            const tolerance = buildTolerance(options);

            return new Promise<PickingResultData>((resolve, reject) => {
                let onAbort: (() => void) | undefined;

                const onKeyDown = (e: KeyboardEvent) => {
                    if (e.key === "Escape") {
                        session.reject(new DOMException("Aborted", "AbortError"));
                    }
                };

                const teardown = () => {
                    if (sessionRef.current === session) {
                        sessionRef.current = null;
                    }
                    if (abortSignal && onAbort) {
                        abortSignal.removeEventListener("abort", onAbort);
                    }
                    window.removeEventListener("keydown", onKeyDown);
                    dispatch(renderActions.stopPicker(Picker.SdkPicking));
                    dispatch(measureActions.selectHoverObj(undefined));
                };

                const session: PickingSession = {
                    tolerance,
                    onHover,
                    resolve: (result) => {
                        teardown();
                        resolve(result);
                    },
                    reject: (reason) => {
                        teardown();
                        reject(reason);
                    },
                };

                if (abortSignal) {
                    onAbort = () => session.reject(new DOMException("Aborted", "AbortError"));
                    abortSignal.addEventListener("abort", onAbort, { once: true });
                }

                window.addEventListener("keydown", onKeyDown);
                sessionRef.current = session;
                dispatch(renderActions.setPicker(Picker.SdkPicking));
            });
        },
        [dispatch, sessionRef, confirmCurrentModeChanges],
    );
}
